import {
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { CountryConfigInjection } from "../../di/CountryConfigInjection";
import { CountryConfigListRequestEntity } from "../../domain/entities/CountryConfigListRequestEntity";
import { CountryConfigFormRequestEntity } from "../../domain/entities/CountryConfigFormRequestEntity";
import { CountryConfigResponseEntity } from "../../domain/entities/CountryConfigResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ────────────────────────────────────────────────────────────────────

class CountryConfigInput implements Disposable {
  public fetchList = new BehaviorSubject<CountryConfigListRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public createCountryConfig = new BehaviorSubject<{
    body?: CountryConfigFormRequestEntity;
  }>({});
  public updateCountryConfig = new BehaviorSubject<{
    body?: CountryConfigFormRequestEntity;
  }>({});
  public deleteCountryConfig = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.createCountryConfig.complete();
    this.updateCountryConfig.complete();
    this.deleteCountryConfig.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class CountryConfigOutput implements Disposable {
  public savedList = new BehaviorSubject<CountryConfigResponseEntity[]>([]);
  public savedDetail =
    new BehaviorSubject<CountryConfigResponseEntity | null>(null);
  public pagination = new BehaviorSubject<Paging>({
    page: 0,
    limit: 10,
    total: 0,
  });
  public actionSuccess = new BehaviorSubject<
    "create" | "update" | "delete" | null
  >(null);
  public onError = new BehaviorSubject<string>("");
  public listLoading = new BehaviorSubject<boolean>(false);
  public formLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.actionSuccess.complete();
    this.onError.complete();
    this.listLoading.complete();
    this.formLoading.complete();
    this.detailLoading.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class CountryConfigViewModel extends BaseViewModel<
  CountryConfigInput,
  CountryConfigOutput
> {
  constructor() {
    super(new CountryConfigInput(), new CountryConfigOutput());
  }

  private useCase = CountryConfigInjection.getCountryConfigUseCase();

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getCountryConfigList(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchDetail, async (id) => {
      if (!id) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getCountryConfigDetail(id);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    // Create
    this.skipFirstInitial(this.input.createCountryConfig, async ({ body }) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.createCountryConfig(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });

    // Update
    this.skipFirstInitial(this.input.updateCountryConfig, async ({ body }) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.updateCountryConfig(body);
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });

    // Delete
    this.skipFirstInitial(this.input.deleteCountryConfig, async (id) => {
      if (!id) return;
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.deleteCountryConfig(id);
        if (res?.data) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });
  }
}
